import React, { useState } from 'react'
import { Row, Col, Form, Spinner } from 'react-bootstrap'
import BackButton from '../../common/buttons/backButton'
import { toast } from 'react-toastify'
import constants from '../../../config/constants'
import { uploadFile, deleteFile } from '../../../services/genericService'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Listing, ButtonName } from '../../../config/enums'
import { capitalize } from '../../../utility/stringUtils'
import AddButtonGroup from './elements/addButtonGroup'
import EditButtonGroup from './elements/editButtonGroup'
import styles from './giftForm.module.css'
import { GiftBundle } from '../../../models/GiftBundle'

interface FormProps {
  formCallback: (
    name: string,
    listingStatus: boolean,
    giftBundles: string[],
    imageUrlFromForm: string
  ) => void
  isDisabled: boolean
  formType: string
  giftBundles: GiftBundle[]
  currentGiftBundle?: string
  isLoading?: boolean
  currentImage?: string
  listingStatus?: boolean
  name?: string
  docId?: string
}

const GiftForm: React.FC<FormProps> = ({
  formCallback,
  isDisabled,
  formType,
  isLoading,
  currentImage,
  listingStatus,
  giftBundles,
  currentGiftBundle,
  name,
  docId
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isChangingImage, setIsChangingImage] = useState(false)

  const errorMsg = constants.inlineError

  const schema = yup.object({
    name: yup
      .string()
      .required(errorMsg.fieldRequired.replace('%s', 'Gift name')),
    listingStatus: yup.boolean(),
    imageUrl: yup.string()
  })

  return (
    <Formik
      data-testid="gift-form"
      enableReinitialize
      validationSchema={schema}
      onSubmit={values => {
        formCallback(
          values.name,
          values.listingStatus,
          values.giftBundle === '' ? [] : [values.giftBundle],
          values.imageUrl
        )
      }}
      initialValues={{
        name: name || '',
        listingStatus: listingStatus!,
        giftBundles: giftBundles,
        giftBundle: currentGiftBundle || '',
        imageUrl: currentImage || ''
      }}
    >
      {({ handleSubmit, values, errors, touched, setFieldValue }) => (
        <>
          <Form onSubmit={handleSubmit} data-testid="gift-form">
            {isLoading ? (
              <Col
                className={`mx-auto text-center ${styles.spinnerCenter}`}
                data-testid="fetch-document-loading"
              >
                <Spinner animation="border" />
              </Col>
            ) : (
              <Col className="mx-auto">
                <Row className="pt-lg-5" data-testid="backbutton">
                  <div className="d-none d-sm-none d-md-none d-lg-inline">
                    <BackButton rootPaths={['/']} size={35} />
                  </div>
                  <h1> {capitalize(formType)} Gift</h1>
                </Row>
                <Row className="pt-5">
                  <Col xs={12} lg={8} className="mx-auto">
                    <Row>
                      <Col>
                        <h5 className="font-gray">PHOTOS</h5>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {isUploading ? (
                          <Spinner
                            animation="border"
                            data-testid="gift-image-loading"
                          />
                        ) : (
                          <>
                            <img
                              src={values.imageUrl || '/image.png'}
                              width="100%"
                              height="80%"
                              alt="img-edit"
                              className={styles.GiftImage}
                              data-testid="gift-image-upload"
                            />
                            {/**
                             * Adds an x mark in the photo which allows the user
                             * to cancel change/upload and delete file in firebase
                             * */

                            isChangingImage ? (
                              <div
                                className={styles.removePhoto}
                                datatest-id="edit-changing-image"
                              >
                                <button>
                                  <h4
                                    onClick={() => {
                                      // delete file from firebase storage
                                      deleteFile(values.imageUrl)
                                      // restore to old file
                                      setFieldValue('imageUrl', currentImage)
                                      // remove X mark in photo
                                      setIsChangingImage(false)
                                    }}
                                  >
                                    X
                                  </h4>
                                </button>
                              </div>
                            ) : null}
                          </>
                        )}

                        <Form.File>
                          <Form.File.Input
                            className="pt-3"
                            id="gift-image"
                            name="logoUrl"
                            data-testid="gift-image"
                            accept="image/*"
                            value=""
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setIsUploading(true)
                              const logo = e.target.files![0]
                              toast.info(
                                constants.alertMessage.uploadingImage,
                                {
                                  autoClose: false
                                }
                              )
                              uploadFile(logo, 'gifts').then(url => {
                                setFieldValue('imageUrl', url!)
                                setIsChangingImage(true)
                                toast.dismiss()
                                setIsUploading(false)
                              })
                            }}
                          />
                        </Form.File>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label htmlFor="gift-name">
                            <h5 className="font-gray">Gift Name</h5>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            id="gift-name"
                            placeholder="Gift Name"
                            value={values.name}
                            data-testid="gift-name"
                            isInvalid={touched.name && !!errors.name}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue('name', e.target.value)
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group>
                          <Form.Label htmlFor="listing-status">
                            <h5 className="font-gray">LISTING STATUS</h5>
                          </Form.Label>
                          <br />
                          <Form.Check
                            type="switch"
                            id="listing-status"
                            data-testid="listing-status"
                            label={
                              values.listingStatus
                                ? Listing.enabled
                                : Listing.disabled
                            }
                            className={`pt-1 pl-4 ml-3 ${styles.labelSwitch}`}
                            checked={values.listingStatus}
                            onChange={() => {
                              if (values.listingStatus) {
                                setFieldValue('listingStatus', false)
                              } else {
                                setFieldValue('listingStatus', true)
                              }
                            }}
                          />
                        </Form.Group>

                        <Form.Group>
                          <Form.Label>Gift Bundles</Form.Label>

                          {/**
                           * ? Added index zero and set multiple to false.
                           * This is to remove the error The `value` prop
                           * supplied to <select> must be a scalar value
                           * console error.
                           * */}

                          <Form.Control
                            as="select"
                            isInvalid={
                              touched.giftBundles &&
                              values.giftBundles.length === 0
                            }
                            onChange={(
                              e: React.FormEvent<HTMLSelectElement>
                            ) => {
                              setFieldValue('giftBundle', e.currentTarget.value)
                            }}
                            value={values.giftBundle}
                            data-testid="giftBundleDropdown"
                          >
                            <option value=""> </option>
                            {values.giftBundles &&
                              values.giftBundles.map((item: GiftBundle) => {
                                return (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                )
                              })}
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.giftBundles}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      {formType === ButtonName.add ? (
                        <AddButtonGroup isDisabled={isDisabled} />
                      ) : (
                        <EditButtonGroup
                          isDisabled={isDisabled}
                          docId={docId!}
                        />
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            )}
          </Form>
        </>
      )}
    </Formik>
  )
}

export default GiftForm
